/**
 * Detects short, opaque error messages thrown by minified React / Next.js
 * internals during RSC flight-data processing or hydration.
 *
 * In production builds, server-component rendering errors are serialised into
 * the RSC flight stream with a numeric digest instead of the original message.
 * When the client deserialises that stream, it creates an Error whose message
 * is the digest (a short string like "fa", "e2b", etc.). React then reports
 * the error via `onRecoverableError` → `reportGlobalError`, which surfaces it
 * in Datadog as an unhandled browser error.
 *
 * These errors:
 *   - originate entirely from framework code (all stack frames resolve to
 *     `@ undefined` — inline/bundled scripts with no source-mapped URL)
 *   - carry no application stack frames
 *   - are not actionable because the original error message and stack were
 *     already logged server-side
 *
 * Matches when:
 *   1. The message is short (≤ 8 chars), contains no spaces, and does not
 *      match a known actionable pattern (e.g. NEXT_REDIRECT).
 *   2. Every stack frame resolves to `@ undefined` (no application source
 *      paths).
 */
export const isMinifiedFrameworkDigestNoise = (
  message: string | undefined,
  stack: string | undefined,
): boolean => {
  if (!message) {
    return false;
  }

  if (message.length > 8 || message.includes(" ")) {
    return false;
  }

  if (
    _KNOWN_ACTIONABLE_SHORT_MESSAGES.has(message) ||
    message.startsWith("E")
  ) {
    return false;
  }

  if (!stack) {
    return true;
  }

  const frames = stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));

  if (frames.length === 0) {
    return true;
  }

  return frames.every((frame) => frame.includes("@ undefined"));
};

const _KNOWN_ACTIONABLE_SHORT_MESSAGES = new Set([
  "NEXT_REDIRECT",
  "NEXT_NOT_FOUND",
]);
